import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Scales, Trash, Eye, MagnifyingGlass } from "@phosphor-icons/react";
import { deleteCase, getCase } from "../lib/api";
import { getCachedCase, setCachedCase } from "../lib/caseCache";
import { useCases, invalidateCases } from "../lib/casesStore";
import useIsDesktop from "../hooks/useIsDesktop";
import MobileCases from "../components/cases/MobileCases";

// P1 — lazy-load the modal + its heavy dependencies (CaseStagesTimeline,
// CitedCassations, shadcn Dialog). Users who never open a case never pay
// their JS/CSS cost.
const CaseDetailModal = lazy(() => import("./caseDashboard/CaseDetailModal"));

export default function CaseDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { cases, loading, refresh } = useCases();

  // Synchronous pre-hydrate: when we land on /cases as part of a "return
  // to case file" navigation, open the CaseDetail modal on the same
  // render tick — no ~200ms flash of empty landing.
  const initialPendingId = (() => {
    if (location.state?.openCaseId) return location.state.openCaseId;
    try { return sessionStorage.getItem("lex.reopenCaseId") || null; }
    catch { return null; }
  })();
  const initialCached = initialPendingId ? getCachedCase(initialPendingId) : null;

  const [selected, setSelected] = useState(initialCached);
  const [open, setOpen] = useState(Boolean(initialCached));

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه القضية؟")) return;
    try {
      await deleteCase(id);
      toast.success("تم الحذف.");
      await invalidateCases();
    } catch {
      toast.error("تعذّر الحذف.");
    }
  };

  const openCase = async (id) => {
    // ⚡ Stale-while-revalidate: cached case → open dialog instantly,
    // refresh in background. Removes 300-800 ms tap-to-open lag on mobile.
    const cached = getCachedCase(id);
    if (cached) {
      setSelected(cached);
      setOpen(true);
    }
    try {
      const data = await getCase(id);
      setSelected(data);
      setOpen(true);
      setCachedCase(id, data);
    } catch {
      if (!cached) toast.error("تعذّر فتح القضية.");
    }
  };

  /* اِستِئناف عَرض تَفاصيل القَضيّة بَعد العَودَة مِن صَفحَة إضافَة/تَعديل مَرحَلَة. */
  useEffect(() => {
    let pendingId = location.state?.openCaseId;
    if (!pendingId) {
      try { pendingId = sessionStorage.getItem("lex.reopenCaseId"); }
      catch { /* private mode */ }
    }
    if (!pendingId) return;
    openCase(pendingId);
    try { sessionStorage.removeItem("lex.reopenCaseId"); } catch { /* no-op */ }
    if (location.state?.openCaseId) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state?.openCaseId]);

  useEffect(() => {
    if (!open) {
      try { sessionStorage.removeItem("lex.reopenCaseId"); } catch { /* no-op */ }
    }
  }, [open]);

  // Surface fetch errors via toast (parity with pre-refactor behaviour).
  const { error } = useCases();
  useEffect(() => {
    if (error) toast.error("تعذّر تحميل القضايا.");
  }, [error]);

  return (
    <>
      <MobileCases onView={openCase} onDelete={handleDelete} />

      {/* P3 — desktop tree is mounted ONLY on lg+ viewports. Previously
          it was always in the DOM with `hidden lg:block`, forcing React
          to diff ~120 lines of JSX on every mobile mount for nothing. */}
      {isDesktop && (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">سجل القضايا</div>
              <h1 className="font-amiri text-4xl lg:text-5xl text-[#0A192F]">قضاياي</h1>
              <p className="mt-3 text-[#4A4A4A]">جميع التحليلات السابقة محفوظة هنا.</p>
            </div>
            <Link to="/analyze" data-testid="cases-new-cta" className="lex-btn-primary inline-flex items-center gap-2">
              <Scales size={18} weight="duotone" />
              <span>قضية جديدة</span>
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              <div className="lex-skeleton h-20" />
              <div className="lex-skeleton h-20" />
              <div className="lex-skeleton h-20" />
            </div>
          ) : cases.length === 0 ? (
            <div className="border border-dashed border-[#EAEAEA] p-16 text-center">
              <MagnifyingGlass size={48} weight="duotone" color="#D4AF37" className="mx-auto" />
              <h3 className="font-amiri text-2xl text-[#0A192F] mt-4">لا توجد قضايا بعد</h3>
              <p className="text-[#4A4A4A] mt-2">ابدأ بتحليل قضيتك الأولى الآن.</p>
              <Link to="/analyze" data-testid="empty-cases-cta" className="lex-btn-gold inline-block mt-6">
                تحليل قضية
              </Link>
            </div>
          ) : (
            <div className="border border-[#EAEAEA]" data-testid="cases-table">
              {cases.map((c) => {
                const an = c.analysis || {};
                const prob = typeof an.success_probability === "number" ? an.success_probability : null;
                return (
                  <div
                    key={c.id}
                    className="flex flex-wrap items-center gap-4 px-6 py-5 border-b border-[#EAEAEA] last:border-0 hover:bg-[#FAF9F6] transition-colors"
                    data-testid={`case-row-${c.id}`}
                  >
                    <div className="flex-1 min-w-[260px]">
                      {c.ref_number && (
                        <div className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-wider text-[#D4AF37] bg-[#FAF9F6] border border-[#D4AF37]/30 mb-1.5" dir="ltr">
                          {c.ref_number}
                        </div>
                      )}
                      <div className="font-amiri text-xl text-[#0A192F] leading-tight">{c.title}</div>
                      <div className="text-xs text-[#8A8A8A] mt-1">
                        {new Date(c.created_at).toLocaleString("ar-IQ", {
                          year: "numeric", month: "short", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}{" · "}
                        {an.case_type || "—"} · {an.competent_court || "—"}
                      </div>
                    </div>
                    {prob !== null && (
                      <div className="text-center min-w-[80px]">
                        <div className="font-amiri text-2xl text-[#0A192F]">{prob}٪</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">احتمالية</div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openCase(c.id)}
                        data-testid={`view-case-${c.id}`}
                        className="p-2 hover:bg-[#FAF9F6] border border-[#EAEAEA]"
                        title="عرض التفاصيل"
                      >
                        <Eye size={18} weight="duotone" color="#0A192F" />
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        data-testid={`delete-case-${c.id}`}
                        className="p-2 hover:bg-[#FAF9F6] border border-[#EAEAEA]"
                        title="حذف"
                      >
                        <Trash size={18} weight="duotone" color="#8B0000" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* P1 — modal is lazy; Suspense fallback is empty because the
          dialog itself is invisible until open=true anyway. */}
      {open && (
        <Suspense fallback={null}>
          <CaseDetailModal open={open} onOpenChange={setOpen} selected={selected} />
        </Suspense>
      )}
    </>
  );
}
